import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Rnd } from "react-rnd";
import AutoSizer from "react-virtualized-auto-sizer";

import { Typography } from "antd";
import classnames from "classnames";
import {
  DEFAULT_FONT_SIZE,
  DEFAULT_ITEM_HEIGHT,
  DEFAULT_LEGEND_MIN_WIDTH,
  DEFAULT_LEGEND_WIDTH,
  DEFAULT_LEGEND_X_POSITION_BUFFER,
  DEFAULT_LEGEND_Y_POSITION,
  DEFAULT_OPACITY,
  HEADING_FONT_SIZE_INCREASE,
  IS_FORECAST_COMPARE_CHART
} from "constants/chart.constants";
import { resolveFontFamily } from "constants/style.constants";
import { useChartsContext } from "contexts";
import { ForecastMode } from "store/features";
import { RootState } from "store/rootReducer";
import styled from "styled-components";
import { isScrollbarClicked } from "utils";

import { useChartEntities } from "hooks/charts";

import { LEGEND_USAGE_TYPES, VirtualizedLegendList } from "components/base";
import { LegendRowProps } from "components/base/VirtualizedLegendList";
import { getLegendItemSize } from "components/screenshot/utils";

import ForecastCompareLegend from "./ForecastCompareLegend";
import ChartLegendItem from "./LegendItem";
import useChartLegendItems from "./hooks/useChartLegendItems";
import useChartLegendTitle from "./hooks/useChartLegendTitle";
import getCalculatedLegend from "./utils/getCalculatedLegend";
import getUpdatedChartSizeLegend from "./utils/getUpdatedChartSizeLegend";

const { Text } = Typography;
const noWellsFoundText = "No entities were found";

export type ParentDimensionsT = { width: number; height: number };
export type LegendPositionT = { x: number; y: number };
export type LegendDimensionsT = { width: number; height: number };

export type LegendT = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type ChartLegendT = {
  className?: string;
  disableDragging?: boolean;
  showLegendBorder?: boolean;
  width?: number;
};
export type Ref = HTMLDivElement;

const ChartLegend = ({
  parentDimensions = undefined,
  id,
  screenshot,
  bounds,
  className = "",
  showLegendBorder = false,
  disableDragging = false,
  width = DEFAULT_LEGEND_WIDTH,
  // How far the DEFAULT (undragged, nothing stored) position sits from the
  // right edge. Charts that draw canvas controls under the NE corner — e.g.
  // rig/frac Activity's right-axis dataZoom slider — pass a larger buffer so
  // this DOM overlay doesn't swallow their pointer events; a canvas control
  // can never paint or click above the legend.
  xPositionBuffer = DEFAULT_LEGEND_X_POSITION_BUFFER,
  legend
}) => {
  // hooks
  const title = useChartLegendTitle();
  const items = useChartLegendItems();

  // Add a ref to keep track of the last non-empty items
  const lastValidItemsRef = useRef(items);

  // Get forecast state for subtitle
  const forecastMode = useSelector((state: RootState) => state.app.forecastMode);
  const { forecast, chartType, apiResponse } = useChartEntities(useChartsContext().id);

  // Check if there's actual forecast series data on the chart
  const hasForecastSeriesData =
    apiResponse?.series?.some((series) => series.isForecast === true) || false;

  // Check if all entities are missing forecast data
  const allEntitiesMissingForecast =
    apiResponse?.messages?.some((msg) => msg.message === "Missing Forecast Data") &&
    apiResponse?.series?.length > 0 &&
    apiResponse?.series?.every((series) => !series.isForecast);

  // Count the forecast sources that can actually be named. Empty ones are dropped rather than
  // merely defined-checked: an unnamed source used to count toward the pair below, so a response
  // carrying one real source plus one unnamed series opened the compare legend and rendered a
  // lone comparison entry — a dotted line with no dashed line to read it against. (EVA-113)
  const uniqueForecastSources = new Set(
    apiResponse?.series
      ?.filter((series) => series.isForecast === true)
      .map((series) => series.forecastSource)
      .filter((source) => Boolean(source))
  );

  const isForecastCompare =
    forecastMode === ForecastMode.Compare &&
    IS_FORECAST_COMPARE_CHART(chartType) &&
    forecast?.active === true &&
    hasForecastSeriesData &&
    !allEntitiesMissingForecast &&
    uniqueForecastSources.size === 2;

  // calculate height of list
  const legendTitleSize = screenshot.visible
    ? Number(screenshot.properties?.preset?.legendTitleSize) || DEFAULT_FONT_SIZE
    : DEFAULT_FONT_SIZE;

  const legendFontSize = screenshot.visible
    ? Number(screenshot.properties?.preset?.legendFontSize) || DEFAULT_FONT_SIZE
    : DEFAULT_FONT_SIZE;

  const headingFontSizePx = `${legendTitleSize + HEADING_FONT_SIZE_INCREASE}px`;
  const itemSize = getLegendItemSize(screenshot.visible, legendTitleSize);
  const itemsHeight = items.length * itemSize;
  const backgroundOpacity = screenshot.visible
    ? Number(screenshot.properties?.preset?.legendOpacity) || DEFAULT_OPACITY
    : DEFAULT_OPACITY;

  const showLegendCounts = screenshot.visible
    ? screenshot.properties?.preset?.legendShowCounts ?? true
    : true;
  const showTypeWellRescat = screenshot.visible
    ? screenshot.properties?.preset?.legendShowTypeWellRescat ?? true
    : true;
  const presetFontFamily = screenshot.properties?.preset?.fontFamily;
  const fontFamilyCss =
    screenshot.visible && presetFontFamily
      ? resolveFontFamily(presetFontFamily)
      : "inherit";
  const isWrappingText = screenshot.visible
    ? screenshot.properties?.preset?.isWrappingText ?? false
    : false;

  const positionStorageKey: string = `${id}-position`;
  const parentDimensionsStorageKey: string = `${id}-parentDimensions`;
  const dimensionsStorageKey = `${id}-dimensions`;
  const getStoredObj = (key: string) => JSON.parse(localStorage.getItem(key));

  const storedPosition: LegendPositionT = getStoredObj(positionStorageKey);
  const storedDimensions: LegendDimensionsT = getStoredObj(dimensionsStorageKey);
  const storedParentDimensions: LegendDimensionsT =
    getStoredObj(parentDimensionsStorageKey) || parentDimensions;

  // state
  const [dimensions, setDimensions] = useState<LegendDimensionsT>(() => {
    const storedDimensions = getStoredObj(dimensionsStorageKey);

    return storedDimensions
      ? storedDimensions
      : { width, height: itemsHeight + DEFAULT_ITEM_HEIGHT }; // 32px for title
  });

  useEffect(() => {
    if (items.length > 0) {
      lastValidItemsRef.current = items;
      if (storedDimensions?.height == DEFAULT_ITEM_HEIGHT) {
        const calculatedItemsHeight = items.length * itemSize;
        setDimensions({
          width,
          height: calculatedItemsHeight + DEFAULT_ITEM_HEIGHT
        });
      }
    }
  }, [items]);

  const [position, setPosition] = useState<LegendPositionT>(() => {
    let currentPosition;
    if (!!storedPosition && !disableDragging) {
      currentPosition = storedPosition;
    } else if (!dimensions || !parentDimensions) {
      currentPosition = { x: 0, y: 0 };
    } else {
      currentPosition = {
        x: parentDimensions?.width - dimensions?.width - xPositionBuffer,
        y: DEFAULT_LEGEND_Y_POSITION
      };
    }
    return currentPosition;
  });

  // Use either current items or last valid items
  const displayItems = items.length > 0 ? items : lastValidItemsRef.current;

  // markup: no wells found
  const noWellsMarkup = displayItems.length ? null : (
    <StyledText type="danger">{noWellsFoundText}</StyledText>
  );

  // markup: react-virtuoso item
  const Row = ({
    legend,
    useCount,
    showTypeWellRescat,
    itemStyle,
    titleStyle
  }: LegendRowProps) => (
    <ChartLegendItem
      useCount={useCount}
      showTypeWellRescat={showTypeWellRescat}
      itemStyle={itemStyle}
      titleStyle={titleStyle}
      key={legend.value}
      legend={legend}
      fontSize={legendFontSize}
      isScreenshotMode={screenshot.visible}
    />
  );

  // handlers
  function handleDragStop(_, data) {
    const nextPosition = { x: data.x, y: data.y };
    setPosition(nextPosition);

    localStorage.setItem(positionStorageKey, JSON.stringify(nextPosition));
    localStorage.setItem(
      parentDimensionsStorageKey,
      JSON.stringify({ width: parentDimensions?.width, height: parentDimensions?.height })
    );
  }

  function handleResize(e, direction, ref, delta, p) {
    const nextDimensions = {
      width: ref.offsetWidth,
      height: ref.offsetHeight
    };
    setDimensions(nextDimensions);
    setPosition(p);

    localStorage.setItem(dimensionsStorageKey, JSON.stringify(nextDimensions));
    localStorage.setItem(positionStorageKey, JSON.stringify(p));
    localStorage.setItem(
      parentDimensionsStorageKey,
      JSON.stringify({ width: parentDimensions?.width, height: parentDimensions?.height })
    );
  }
  function calculateLegendPosition() {
    if (!!storedPosition && !disableDragging) {
      const updatedLegend =
        storedParentDimensions.width != parentDimensions.width ||
        storedParentDimensions.height != parentDimensions.height
          ? getUpdatedChartSizeLegend(
              storedParentDimensions,
              parentDimensions,
              storedPosition,
              storedDimensions,
              currentDimensions
            )
          : currentLegend;
      const calculatedLegend = getCalculatedLegend(updatedLegend, parentDimensions);
      return { x: calculatedLegend.x, y: calculatedLegend.y };
    } else if (!dimensions || !parentDimensions) {
      return { x: 0, y: 0 };
    } else {
      return {
        x: parentDimensions?.width - dimensions?.width - xPositionBuffer,
        y: DEFAULT_LEGEND_Y_POSITION
      };
    }
  }
  // derived classes and styles
  const wrapperClassNames = classnames("chart-legend", className, legend?.position);

  const currentLegend: LegendT = {
    ...position,
    ...dimensions
  };

  // update the currentDimensions for rendering the legend
  const currentDimensions: LegendDimensionsT = useMemo(() => {
    const calculatedDimensions = getCalculatedLegend(currentLegend, parentDimensions);
    return { width: calculatedDimensions.width, height: calculatedDimensions.height };
  }, [dimensions]);

  // update currentPosition for rendering the legend
  const currentPosition: LegendPositionT = useMemo(() => {
    return calculateLegendPosition();
  }, [position]);

  // update legend dimensions in local storage for future renders
  useEffect(() => {
    localStorage.setItem(dimensionsStorageKey, JSON.stringify(currentDimensions));
  }, [currentDimensions]);

  // update legend position in local storage for future renders
  useEffect(() => {
    localStorage.setItem(positionStorageKey, JSON.stringify(currentPosition));
  }, [currentPosition]);

  // setPosition and setDimensions when changing parent dimensions as handleDragStop and handleResize does not account for parent dimension changes
  // also update parent dimensions and legend dimensions in local storage for future renders
  useEffect(() => {
    const currentPosition = calculateLegendPosition();

    setPosition(currentPosition);
    setDimensions(currentDimensions);

    localStorage.setItem(
      parentDimensionsStorageKey,
      JSON.stringify({ width: parentDimensions?.width, height: parentDimensions?.height })
    );
    localStorage.setItem(dimensionsStorageKey, JSON.stringify(currentDimensions));
  }, [parentDimensions]);

  if (!bounds) return null;

  return (
    <StyledRnd
      data-testid="chart-legend"
      bounds="parent"
      className={wrapperClassNames}
      disableDragging={disableDragging}
      minWidth={DEFAULT_LEGEND_MIN_WIDTH}
      onDragStop={handleDragStop}
      onResize={handleResize}
      position={{ x: currentPosition.x, y: currentPosition.y }}
      size={{ width: currentDimensions.width, height: currentDimensions.height }}
      backgroundopacity={backgroundOpacity}
      $showLegendBorder={screenshot?.active ? showLegendBorder : true}>
      <Wrapper
        style={{ fontFamily: fontFamilyCss }}
        onMouseDown={(event) => {
          if (isScrollbarClicked(event)) {
            event.stopPropagation();
            event.preventDefault();
          }
        }}>
        <StyledTitle style={{ fontSize: headingFontSizePx }}>{title}</StyledTitle>
        {isForecastCompare && <ForecastCompareLegend />}
        {noWellsMarkup}
        <LegendContent>
          <AutoSizer>
            {({ width, height }) => (
              <VirtualizedLegendList
                items={displayItems}
                width={width}
                height={height}
                showCounts={showLegendCounts}
                showTypeWellRescat={showTypeWellRescat}
                isWrappingText={isWrappingText}
                fontSize={legendFontSize}
                legendUsage={LEGEND_USAGE_TYPES.CHART}
                itemRenderer={Row}
              />
            )}
          </AutoSizer>
        </LegendContent>
      </Wrapper>
    </StyledRnd>
  );
};

export default ChartLegend;
ChartLegend.displayName = "ChartLegend";

const StyledRnd = styled(Rnd)`
  position: absolute;
  margin: 10px;
  background: ${(props) =>
    props.backgroundopacity === undefined
      ? "White"
      : `rgba(255, 255, 255, ${props.backgroundopacity})`};
  border: ${(props: { $showLegendBorder: boolean }) =>
    props.$showLegendBorder ? "1px solid gray" : ""};
  overflow: hidden;
  z-index: 21; // z-index higher than the confirm indicators to stop flickering
  min-height: 35px;

  & > div:last-child {
    position: unset;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: inline-flex;
  flex-direction: column;
  opacity: 1;
  padding-right: 7px;
  padding-bottom: 6px;
`;

const LegendContent = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
`;

const StyledTitle = styled.span`
  font-weight: 600;
  text-align: left;
  padding: 6px 12px;
  margin-bottom: 0 !important;
`;
const StyledText = styled(Text)`
  text-align: center;
  padding: 4px 16px;
`;
